(() => {
  if (customElements.get('cm-purchase-bar')) return;
  // Texte in der Sprache der Seite: window.cmT.purchase_bar aus snippets/cm-x-js-texts (Texte in snippets/cm-xt-js, Abschnitt purchase_bar).
  // Fehlt cmT oder ein Schlüssel, gilt der deutsche Text hier; {name} wird ersetzt.
  const cmTexts = (window.cmT && window.cmT.purchase_bar) || {};
  const tx = (key, de, vars) => {
    const own = cmTexts[key];
    const text = typeof own === 'string' && own && !/^(translation missing|\[text fehlt)/i.test(own) ? own : de;
    return vars ? text.replace(/\{(\w+)\}/g, (all, name) => (name in vars ? vars[name] : all)) : text;
  };
  // Woran der Knopf von Shopify „nicht kaufbar“ erkennen lässt: Wörter der Sprache, mit | getrennt (kein regulärer Ausdruck).
  const unavailableWords = tx('unavailable_words', 'ausverkauft|nicht verfügbar|nicht lieferbar').split('|').map(word => word.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).filter(Boolean);
  const unavailablePattern = unavailableWords.length ? new RegExp(unavailableWords.join('|'), 'i') : /ausverkauft|nicht verfügbar|nicht lieferbar/i;
  customElements.define('cm-purchase-bar', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      if (!this.product) return;
      this.abort = new AbortController();
      this.dock = this.querySelector('.cm-purchase-dock');
      this.total = this.querySelector('.cm-purchase-total');
      this.action = this.querySelector('.cm-purchase-action');
      this.queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      this.observer = new MutationObserver(records => {
        // Ignore our own mirrors: observing them would create an update loop.
        if (records.some(record => !this.contains(record.target))) this.queue();
      });
      this.observer.observe(this.product, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      const opts = { signal: this.abort.signal };
      this.product.addEventListener('input', this.queue, opts);
      this.product.addEventListener('change', this.queue, opts);
      document.addEventListener('cm:price-mode', this.queue, opts);
      document.addEventListener('focusin', this.queue, opts);
      document.addEventListener('focusout', this.queue, opts);
      window.addEventListener('scroll', this.queue, { ...opts, passive: true });
      window.addEventListener('resize', this.queue, opts);
      window.visualViewport?.addEventListener('resize', this.queue, opts);
      this.action.addEventListener('click', () => {
        this.sync();
        if (!this.action.disabled) this.submit?.click();
      }, opts);
      this.queue();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.product?.classList.remove('cm-purchase-ready');
      document.documentElement.classList.remove('cm-purchase-docked');
    }

    sync() {
      this.submit = this.product.querySelector('product-form .product-form__submit');
      const price = this.product.querySelector(`[id="price-${this.dataset.section}"]`);
      const sale = price?.querySelector('.price--on-sale');
      const pair = price?.querySelector(sale ? '.price__sale .price-item--sale .cm-price-pair' : '.price__regular .cm-price-pair');
      if (!this.submit || !pair || price.classList.contains('hidden')) {
        this.hidden = true;
        if (this.product.classList.contains('cm-purchase-ready')) this.product.classList.remove('cm-purchase-ready');
        document.documentElement.classList.remove('cm-purchase-docked');
        return;
      }
      this.hidden = false;
      if (!this.product.classList.contains('cm-purchase-ready')) this.product.classList.add('cm-purchase-ready');
      const disabled = this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true';
      const nativeLabel = this.submit.textContent.trim();
      const unavailable = unavailablePattern.test(nativeLabel);
      const busy = disabled && !unavailable;
      this.action.disabled = disabled;
      this.action.textContent = busy ? tx('updating', 'Wird aktualisiert …') : unavailable ? nativeLabel : tx('add', 'In den Warenkorb');
      const status = busy ? tx('price_updating', 'Preis wird aktualisiert …') : unavailable ? nativeLabel : '';
      this.querySelectorAll('[data-purchase-status], [data-purchase-dock-status]').forEach(node => {
        if (node.textContent !== status) node.textContent = status;
      });
      // Exact server-rendered price pair, including customer-specific net/gross and tax labels.
      // Do not calculate from chc-data or replace the native price/form.
      const html = busy ? '' : pair.outerHTML;
      this.querySelectorAll('[data-purchase-inline-price], [data-purchase-dock-price]').forEach(node => {
        if (node.innerHTML !== html) node.innerHTML = html;
      });
      const labels = [...this.product.querySelectorAll('.chc-label strong')].map(node => node.textContent.trim());
      const summary = labels.join(' · ') || this.product.querySelector('h1')?.textContent.trim() || '';
      this.querySelectorAll('[data-purchase-selection], [data-purchase-dock-selection]').forEach(node => {
        if (node.textContent !== summary) node.textContent = summary;
      });
      const quantity = this.product.querySelector('.quantity__input');
      const count = Math.max(1, Number(quantity?.value) || 1);
      const unit = count === 1 ? tx('unit', 'Preis pro Gerät') : tx('unit_quantity', 'Preis pro Gerät · Menge {count}', { count });
      this.querySelectorAll('[data-purchase-unit]').forEach(node => {
        if (node.textContent !== unit) node.textContent = unit;
      });
      const header = document.querySelector('.cm-header');
      const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 80);
      const totalRect = this.total.getBoundingClientRect();
      const totalVisible = totalRect.top >= headerBottom && totalRect.bottom < window.innerHeight - 12;
      const editing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '');
      // Erst Zubehör und Leasing, dann der Knopf (Inhaber 04.10.2026): die Leiste kommt erst, wenn das erste davon
      // („Dazu passend“, „Passendes Zubehör“, Leasing-Zeile; ohne alles „Deine Auswahl“) gut im Bild war — danach bleibt
      // sie für diese Seite freigegeben, auch beim Zurückscrollen zur Konfiguration.
      if (!this.seen) {
        const shown = [...this.product.querySelectorAll('.cm-es, [data-cm-upsell], .cm-leasing-slot')].find(node => node.getBoundingClientRect().height > 0);
        const gate = shown || this.total;
        this.seen = totalVisible || gate.getBoundingClientRect().top < window.innerHeight * 0.6;
      }
      const floating = this.seen && price.getBoundingClientRect().bottom < headerBottom && !totalVisible && this.product.getBoundingClientRect().bottom > headerBottom && !editing;
      this.dock.hidden = !floating;
      document.documentElement.classList.toggle('cm-purchase-docked', floating);
      if (floating) document.documentElement.style.setProperty('--cm-purchase-height', `${Math.ceil(this.dock.getBoundingClientRect().height)}px`);
    }
  });
})();
